/**
 * Directing the edit from the transcript. The creator picks words and marks
 * them Remove (take them out of the edit), Short (make them into a short) or
 * Note (a comment on what's said). Remove and Short are saved as range notes
 * in the categories `remove` and `short`; notes keep their own category. Each
 * is timed on the timeline from the words' own times, so it shows in the
 * notes list, on the timeline, in the editor's tasks and in the rough cut
 * like any other note.
 *
 * Everything here is pure, so the rules are tested on their own:
 *  - the transcript's words on the timeline (a multicam set's sound angle,
 *    placed by its sync); words trimmed off that angle can't be marked;
 *  - a run of words → the times and quote a mark is saved with;
 *  - which words each mark covers, judged by each word's middle, so a mark
 *    made from words shows on exactly those words, whatever the lines are,
 *    and still lands right if the transcript is made again;
 *  - the overlap rules (planDirection): a Remove merges with the Removes it
 *    overlaps or touches, because two Removes over the same words are one
 *    instruction; a Short may overlap anything except its exact twin,
 *    because two shorts from one moment (a 15-second teaser and a full
 *    minute) are two deliverables; and a Remove may overlap a Short, since a
 *    tangent cut from the episode can still make a good short.
 */

import type { TranscriptLineData, TranscriptWordData } from '../graphqlService';
import { DirectionCategoryId, directionCategoryOf, getAnnotationCategory } from './annotationCategories';
import { TimelineLine, TranscriptPlacement, placeOnTimeline } from './transcript';

export type TranscriptMarkKind = DirectionCategoryId | 'note';

// ---------------------------------------------------------------------------
// Words on the timeline
// ---------------------------------------------------------------------------

export interface MarkableWord {
  /** Position in the transcript's words, in time order. */
  index: number;
  /** The transcript line the word is in (TimelineLine.index). */
  lineIndex: number;
  text: string;
  /** Shown with a space before it (false at the start of a line and in languages written without spaces). */
  spaceBefore: boolean;
  /** Timeline seconds; null when the word is trimmed off the timeline, so it can't be marked. */
  start: number | null;
  end: number | null;
  /** The same for the same word while the transcript grows chunk by chunk. */
  key: string;
}

export interface MarkableLine extends TimelineLine {
  /** First and last of the line's words (inclusive) in `MarkableTranscript.words`. */
  firstWord: number;
  lastWord: number;
}

export interface MarkableTranscript {
  lines: MarkableLine[];
  words: MarkableWord[];
}

/**
 * A line's words with their file times. The API sends them with each line;
 * a line without them (never expected) is split at its spaces, with its time
 * shared out by length, so it can still be marked roughly.
 */
const wordsOfLine = (line: TranscriptLineData): TranscriptWordData[] => {
  const given = (line.words ?? []).filter(
    (word) => word && String(word.text ?? '').trim() !== '' && Number.isFinite(Number(word.start))
  );
  if (given.length > 0) {
    return given.map((word) => {
      const start = Number(word.start);
      const end = Number(word.end);
      return { text: String(word.text).trim(), start, end: Number.isFinite(end) ? Math.max(start, end) : start };
    });
  }
  const tokens = String(line.text ?? '').split(/\s+/).filter(Boolean);
  if (tokens.length === 0 || !Number.isFinite(line.start)) return [];
  const length = Math.max(0, (Number(line.end) || line.start) - line.start);
  const total = tokens.reduce((sum, token) => sum + token.length + 1, 0);
  let at = line.start;
  return tokens.map((text) => {
    const share = (length * (text.length + 1)) / total;
    const word = { text, start: at, end: at + share };
    at += share;
    return word;
  });
};

/**
 * Whether each word follows a space in the line's text. The API trims its
 * words, but the line's text keeps Whisper's spacing (a word that continues
 * the one before has no space, and Japanese or Chinese have none at all).
 */
export const spacesBefore = (text: string, words: string[]): boolean[] => {
  const source = String(text ?? '');
  const out: boolean[] = [];
  let at = 0;
  words.forEach((word, i) => {
    let next = at;
    let space = false;
    while (next < source.length && /\s/.test(source.charAt(next))) {
      next += 1;
      space = true;
    }
    if (word && source.substr(next, word.length) === word) {
      out.push(i > 0 && space);
      at = next + word.length;
      return;
    }
    // Not where it was expected: a space between words, and carry on after it if it's further on.
    out.push(i > 0);
    const found = word ? source.indexOf(word, at) : -1;
    if (found >= 0) at = found + word.length;
  });
  return out;
};

/**
 * The transcript's words on the timeline, and its lines with the range of
 * their words. Words before a trimmed angle's in point or after its out point
 * stay in their line, unmarkable; a line with no word on the timeline is left
 * out, as the plain transcript does. A word that runs past the out point ends
 * there.
 */
export const buildMarkableTranscript = (
  rawLines: TranscriptLineData[],
  placement?: TranscriptPlacement | null
): MarkableTranscript => {
  const lines: MarkableLine[] = [];
  const words: MarkableWord[] = [];
  const outMs = placement && typeof placement.sourceOutMs === 'number' && placement.sourceOutMs > 0 ? placement.sourceOutMs : null;
  const angleEnd = outMs !== null ? placeOnTimeline(outMs / 1000, placement) : null;

  rawLines.forEach((line, lineIndex) => {
    const source = wordsOfLine(line);
    const placed = source.map((word) => {
      const start = placeOnTimeline(word.start, placement);
      if (start === null) return { start: null, end: null };
      const end = placeOnTimeline(word.end, placement) ?? (angleEnd ?? start);
      return { start, end: Math.max(start, end) };
    });
    if (!placed.some((position) => position.start !== null)) return;

    const spaces = spacesBefore(line.text, source.map((word) => word.text));
    const firstWord = words.length;
    const timed: TranscriptWordData[] = [];
    source.forEach((word, i) => {
      const { start, end } = placed[i];
      words.push({
        index: words.length,
        lineIndex,
        text: word.text,
        spaceBefore: spaces[i],
        start,
        end,
        key: `${Math.round(word.start * 1000)}:${word.text}`,
      });
      if (start !== null && end !== null) timed.push({ text: word.text, start, end });
    });
    lines.push({
      index: lineIndex,
      start: timed[0].start,
      end: timed.reduce((latest, word) => Math.max(latest, word.end), timed[0].start),
      text: line.text,
      words: timed,
      firstWord,
      lastWord: words.length - 1,
    });
  });
  return { lines, words };
};

/** The line holding a word (lines in word order), or -1. */
export const linePositionOfWord = (lines: MarkableLine[], wordIndex: number): number => {
  let low = 0;
  let high = lines.length - 1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const line = lines[middle];
    if (wordIndex < line.firstWord) high = middle - 1;
    else if (wordIndex > line.lastWord) low = middle + 1;
    else return middle;
  }
  return -1;
};

/** The nearest word that can be marked, from `index` in `direction` (1 or -1), or -1. */
export const markableWordFrom = (words: MarkableWord[], index: number, direction: 1 | -1): number => {
  for (let i = index; i >= 0 && i < words.length; i += direction) {
    if (words[i].start !== null) return i;
  }
  return -1;
};

// ---------------------------------------------------------------------------
// A selection of words
// ---------------------------------------------------------------------------

/** The shortest mark: a word said in a flash still covers a frame or three. */
export const MIN_MARK_SECONDS = 0.1;
/** Quotes longer than this keep their start and end, with "…" between. */
export const MAX_QUOTE_CHARS = 200;
const QUOTE_HEAD_CHARS = 120;
const QUOTE_TAIL_CHARS = 60;

/** Words as text: a space where the line has one, and between lines. */
const joinWords = (list: MarkableWord[]): string =>
  list
    .map((word, i) => (i > 0 && (word.spaceBefore || word.lineIndex !== list[i - 1].lineIndex) ? ` ${word.text}` : word.text))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();

/** The words from `from` to `to` (those on the timeline) as one line of text, shortened in the middle when long. */
export const quoteOfWords = (words: MarkableWord[], from: number, to: number, max = MAX_QUOTE_CHARS): string => {
  const list = words.slice(Math.max(0, from), Math.max(0, to) + 1).filter((word) => word.start !== null);
  const full = joinWords(list);
  if (full.length <= max) return full;
  const head: MarkableWord[] = [];
  let length = 0;
  for (let i = 0; i < list.length && length < QUOTE_HEAD_CHARS; i += 1) {
    head.push(list[i]);
    length += list[i].text.length + 1;
  }
  const tail: MarkableWord[] = [];
  length = 0;
  for (let i = list.length - 1; i >= head.length && length < QUOTE_TAIL_CHARS; i -= 1) {
    tail.unshift(list[i]);
    length += list[i].text.length + 1;
  }
  const shortened = tail.length > 0 ? `${joinWords(head)} … ${joinWords(tail)}` : joinWords(head);
  // One very long piece (speech recognition without word times) is cut by characters.
  return shortened.length > max + QUOTE_TAIL_CHARS ? `${shortened.slice(0, max).trim()}…` : shortened;
};

export interface WordSpan {
  /** First and last selected word that is on the timeline. */
  from: number;
  to: number;
  /** Timeline seconds: the first word's start to the last word's end. */
  start: number;
  end: number;
  quote: string;
  wordCount: number;
}

/**
 * The words between `a` and `b` (either order) that can be marked, as the
 * times and quote a mark is saved with; null when none of them can be.
 * Trimmed words only sit at the edges of an angle, so they are dropped from
 * the ends of the selection.
 */
export const spanOfWords = (words: MarkableWord[], a: number, b: number): WordSpan | null => {
  if (words.length === 0 || !Number.isFinite(a) || !Number.isFinite(b)) return null;
  let from = Math.max(0, Math.min(a, b));
  let to = Math.min(words.length - 1, Math.max(a, b));
  while (from <= to && words[from].start === null) from += 1;
  while (to >= from && words[to].start === null) to -= 1;
  if (from > to) return null;
  const start = words[from].start as number;
  let end = start;
  let wordCount = 0;
  for (let i = from; i <= to; i += 1) {
    const word = words[i];
    if (word.start === null) continue;
    wordCount += 1;
    end = Math.max(end, word.end ?? word.start);
  }
  if (end - start < MIN_MARK_SECONDS) end = start + MIN_MARK_SECONDS;
  return { from, to, start, end, quote: quoteOfWords(words, from, to), wordCount };
};

// ---------------------------------------------------------------------------
// Mark text: “the quote” — an optional comment
// ---------------------------------------------------------------------------

/** “quote”, or “quote” — comment. The same shape as a note started from a transcript line. */
export const formatMarkText = (quote: string, comment?: string | null): string => {
  const words = String(quote ?? '').replace(/\s+/g, ' ').trim();
  const note = String(comment ?? '').trim();
  return note ? `“${words}” — ${note}` : `“${words}”`;
};

/** The quote and the comment of a mark's text; a text without a quote is all comment. */
export const parseMarkText = (text: string): { quote: string | null; comment: string } => {
  const value = String(text ?? '').trim();
  if (value.charAt(0) !== '“') return { quote: null, comment: value };
  const separator = value.indexOf('” — ');
  if (separator > 0) return { quote: value.slice(1, separator).trim(), comment: value.slice(separator + 4).trim() };
  if (value.charAt(value.length - 1) === '”') return { quote: value.slice(1, -1).trim(), comment: '' };
  return { quote: null, comment: value };
};

// ---------------------------------------------------------------------------
// Marks in the transcript
// ---------------------------------------------------------------------------

export interface TranscriptMark {
  id: string;
  kind: TranscriptMarkKind;
  /** Timeline seconds. */
  start: number;
  /** Timeline seconds; the same as `start` for a point note. */
  end: number;
  /** A note at one moment, with no range. */
  point: boolean;
  text: string;
  /** "Remove", "Short", or the note's category. */
  label: string;
  color: string;
}

/** A note as the studios hold it (seconds on the timeline). */
export interface MarkSource {
  id: string;
  time: number;
  endTime?: number | null;
  text: string;
  category?: string | null;
  color?: string | null;
}

/** The notes as marks, in time order. Camera cuts are not passed in. */
export const transcriptMarksFrom = (notes: MarkSource[]): TranscriptMark[] =>
  notes
    .filter((note) => Boolean(note) && Boolean(note.id) && Number.isFinite(Number(note.time)))
    .map((note): TranscriptMark => {
      const start = Math.max(0, Number(note.time));
      const endTime = Number(note.endTime);
      const range = note.endTime !== null && note.endTime !== undefined && Number.isFinite(endTime) && endTime > start;
      const category = getAnnotationCategory(note.category, note.color);
      return {
        id: note.id,
        kind: directionCategoryOf(note.category) ?? 'note',
        start,
        end: range ? endTime : start,
        point: !range,
        text: String(note.text ?? ''),
        label: category.label,
        color: category.color,
      };
    })
    .sort((a, b) => a.start - b.start || a.end - b.end);

/** Lets a word whose middle sits on a mark's edge, give or take float noise, count as in it. */
const EDGE_SECONDS = 0.02;
/** How far before a mark a word can start and still have its middle in the mark (one long piece without word times). */
const LONGEST_WORD_SECONDS = 60;
/** A point note shows on the next word only when that word follows within this long. */
const POINT_NOTE_REACH_SECONDS = 1.5;

export interface MarkCoverage {
  /** The words each mark covers: its first and last word index. Marks over silence have none. */
  range: Map<string, { from: number; to: number }>;
  /** The marks covering each word, by word index. */
  byWord: Map<number, TranscriptMark[]>;
  /** Point notes, shown just before the word they fall on. */
  pointsBefore: Map<number, TranscriptMark[]>;
}

/** First position in `timed` whose start is at or after `seconds`. */
const firstStartingFrom = (timed: MarkableWord[], seconds: number): number => {
  let low = 0;
  let high = timed.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if ((timed[middle].start as number) < seconds) low = middle + 1;
    else high = middle;
  }
  return low;
};

const pushTo = <T>(map: Map<number, T[]>, key: number, value: T) => {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
};

/** Which words each mark covers (by each word's middle), and where point notes go. */
export const coverMarks = (words: MarkableWord[], marks: TranscriptMark[]): MarkCoverage => {
  const timed = words.filter((word) => word.start !== null && word.end !== null);
  const coverage: MarkCoverage = { range: new Map(), byWord: new Map(), pointsBefore: new Map() };
  marks.forEach((mark) => {
    if (mark.point) {
      // The word being said at that moment, or the one that follows soon after.
      let i = firstStartingFrom(timed, mark.start - LONGEST_WORD_SECONDS);
      while (i < timed.length && (timed[i].end as number) <= mark.start) i += 1;
      if (i < timed.length && (timed[i].start as number) - mark.start <= POINT_NOTE_REACH_SECONDS) {
        pushTo(coverage.pointsBefore, timed[i].index, mark);
        coverage.range.set(mark.id, { from: timed[i].index, to: timed[i].index });
      }
      return;
    }
    let from = -1;
    let to = -1;
    for (let i = firstStartingFrom(timed, mark.start - LONGEST_WORD_SECONDS); i < timed.length; i += 1) {
      const word = timed[i];
      const start = word.start as number;
      if (start > mark.end + EDGE_SECONDS) break;
      const middle = (start + (word.end as number)) / 2;
      if (middle < mark.start - EDGE_SECONDS || middle > mark.end + EDGE_SECONDS) continue;
      pushTo(coverage.byWord, word.index, mark);
      if (from < 0) from = word.index;
      to = word.index;
    }
    if (from >= 0) coverage.range.set(mark.id, { from, to });
  });
  return coverage;
};

// ---------------------------------------------------------------------------
// Overlap rules for Remove and Short
// ---------------------------------------------------------------------------

/** Removes this close (or touching word to word) are one stretch to lift. */
const MERGE_GAP_SECONDS = 1;
const SAME_TIME_SECONDS = 0.05;

export type DirectionPlan =
  /** Save a new mark; `replaces` are the Removes it absorbs (delete them once it is saved). */
  | { action: 'create'; kind: DirectionCategoryId; start: number; end: number; text: string; replaces: TranscriptMark[] }
  /** Nothing to save: this mark already covers the words. */
  | { action: 'exists'; mark: TranscriptMark };

/**
 * What marking a selection as Remove or Short does, given the marks there:
 *  - Remove: words already inside one Remove change nothing. Otherwise the
 *    new Remove absorbs every Remove it overlaps, or touches word to word
 *    with less than a second between them: the stretch to lift is their
 *    union, quoted afresh, and their comments are kept.
 *  - Short: the same words as an existing Short change nothing; any other
 *    overlap is a new short.
 *  - Neither merges with the other kind, or with notes.
 */
export const planDirection = (
  kind: DirectionCategoryId,
  span: WordSpan,
  words: MarkableWord[],
  marks: TranscriptMark[],
  coverage: MarkCoverage
): DirectionPlan => {
  const sameKind = marks.filter((mark) => mark.kind === kind);
  const sameWords = (mark: TranscriptMark) => {
    const range = coverage.range.get(mark.id);
    if (range) return range.from === span.from && range.to === span.to;
    return Math.abs(mark.start - span.start) < SAME_TIME_SECONDS && Math.abs(mark.end - span.end) < SAME_TIME_SECONDS;
  };

  if (kind === 'short') {
    const twin = sameKind.find((mark) => !mark.point && sameWords(mark));
    if (twin) return { action: 'exists', mark: twin };
    return { action: 'create', kind, start: span.start, end: span.end, text: formatMarkText(span.quote), replaces: [] };
  }

  const touching = sameKind.filter((mark) => {
    if (mark.start < span.end && mark.end > span.start) return true;
    const range = coverage.range.get(mark.id);
    if (!range) return false;
    const gap = mark.end <= span.start ? span.start - mark.end : mark.start - span.end;
    return range.from <= span.to + 1 && range.to >= span.from - 1 && gap <= MERGE_GAP_SECONDS;
  });
  const container = touching.find((mark) => {
    const range = coverage.range.get(mark.id);
    return range
      ? range.from <= span.from && range.to >= span.to
      : mark.start <= span.start + SAME_TIME_SECONDS && mark.end >= span.end - SAME_TIME_SECONDS;
  });
  if (container) return { action: 'exists', mark: container };
  if (touching.length === 0) {
    return { action: 'create', kind, start: span.start, end: span.end, text: formatMarkText(span.quote), replaces: [] };
  }

  let { from, to, start, end } = span;
  touching.forEach((mark) => {
    start = Math.min(start, mark.start);
    end = Math.max(end, mark.end);
    const range = coverage.range.get(mark.id);
    if (range) {
      from = Math.min(from, range.from);
      to = Math.max(to, range.to);
    }
  });
  const comments = touching.map((mark) => parseMarkText(mark.text).comment).filter(Boolean);
  return {
    action: 'create',
    kind,
    start,
    end,
    text: formatMarkText(quoteOfWords(words, from, to), comments.join('; ')),
    replaces: touching,
  };
};

// ---------------------------------------------------------------------------
// How each word is drawn
// ---------------------------------------------------------------------------

export interface WordDecor {
  removed: boolean;
  short: boolean;
  /** The colour of the note underlining the word, if one does. */
  noteColor: string | null;
  /** The mark a click on the word selects: the shortest one covering it. */
  markId: string | null;
  /** Part of the mark selected in the studio. */
  inSelectedMark: boolean;
  /** Point notes shown just before the word. */
  points: TranscriptMark[];
}

const PLAIN: WordDecor = { removed: false, short: false, noteColor: null, markId: null, inSelectedMark: false, points: [] };

/** How each of a line's words is drawn, and a key that changes only when that does. */
export const decorateLine = (
  line: MarkableLine,
  coverage: MarkCoverage,
  selectedMarkId: string | null
): { decor: WordDecor[]; key: string } => {
  const decor: WordDecor[] = [];
  const parts: string[] = [];
  for (let index = line.firstWord; index <= line.lastWord; index += 1) {
    const covering = coverage.byWord.get(index);
    const points = coverage.pointsBefore.get(index) ?? [];
    if (!covering && points.length === 0) {
      decor.push(PLAIN);
      continue;
    }
    const list = covering ?? [];
    let shortest: TranscriptMark | null = null;
    let noteColor: string | null = null;
    let removed = false;
    let short = false;
    let inSelectedMark = points.some((mark) => mark.id === selectedMarkId);
    for (let i = 0; i < list.length; i += 1) {
      const mark = list[i];
      if (mark.kind === 'remove') removed = true;
      else if (mark.kind === 'short') short = true;
      else if (noteColor === null) noteColor = mark.color;
      if (shortest === null || mark.end - mark.start < shortest.end - shortest.start) shortest = mark;
      if (mark.id === selectedMarkId) inSelectedMark = true;
    }
    const markId = shortest === null ? null : shortest.id;
    decor.push({ removed, short, noteColor, markId, inSelectedMark, points });
    parts.push(`${index}:${removed ? 'r' : ''}${short ? 's' : ''}${noteColor ?? ''}${markId ?? ''}${inSelectedMark ? '*' : ''}${points.map((mark) => mark.id).join(',')}`);
  }
  return { decor, key: parts.join('|') };
};

/** Every word of a query (case- and accent-insensitive), for marking matches word by word. */
export const queryTerms = (query: string): string[] =>
  query.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().split(/\s+/).filter(Boolean);

export const wordMatches = (text: string, terms: string[]): boolean => {
  if (terms.length === 0) return false;
  const folded = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  return terms.some((term) => folded.includes(term));
};
